import type { TransactionFlag } from '@/features/payouts/transaction-flag'
import {
  mapTransactionFlagResourceToTransactionFlag,
  mapTransactionFlagToResource,
  mapTransactionSourceTypeResourceToTransactionSourceType,
  mapTransactionTypeResourceToTransactionType,
  mapUserTransactionReasonResourceToUserTransactionReason,
} from '@/features/payouts/transaction-mappers'
import { type PayoutsAdminApi } from '@/features/payouts/api/admin-api'
import type { PrimeUserFn, PrimeWorkspaceFn } from '@/features/iam'
import { collection } from '@/infra/collection'
import { type AdminHistoricalTransactionResource } from '@/features/payouts/api/resources/admin-transactions'
import {
  AdminTransaction,
  type AdminTransactionProps,
} from './AdminTransaction'
import { type CursorPaged, type CursorPageQuery } from '@/infra/paging'
import { Id } from '@/infra/id'
import type { TransactionDateRange } from '@/features/payouts/transaction-date-range'
import { maybeParseDate } from '@/utils/date-util'

/**
 * Manages payout history from an admin perspective.
 */
export class AdminPayoutHistoryStore {
  /**
   * Transactions.
   */
  readonly transactions = collection({
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
    create: () => new AdminTransaction(),
  })

  constructor(
    private readonly _api: PayoutsAdminApi,
    private readonly _primeUser: PrimeUserFn,
    private readonly _primeWorkspace: PrimeWorkspaceFn,
  ) {}

  /**
   * Lists historical transactions.
   *
   * @param query
   * @param signal
   */
  async listHistoricalTransactions(
    query: ListHistoricalTransactionsQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<AdminTransaction>> {
    const response = await this._api.transactions.listHistoricalTransactions(
      {
        page_size: query.limit,
        page_token: query.after,
        recipient_user_id: query.recipientUserId,
        workspace_ids: query.workspaceIds?.length
          ? query.workspaceIds?.join(',')
          : undefined,
        date_completed_from: query.dateCompleted?.from ?? undefined,
        date_completed_to: query.dateCompleted?.to ?? undefined,
        search_text: query.searchText,
        flags: query.flags?.map(mapTransactionFlagToResource).join(','),
        order_by: 'date_completed desc',
      },
      signal,
    )

    return {
      next: response.next_page_token,
      total: response.total_size,
      data: this.transactions.set(
        response.data.map((r) =>
          this._mapAdminHistoricalTransactionResourceToProps(r),
        ),
      ),
    }
  }

  /**
   * Downloads the historical transactions as a CSV.
   *
   * @param query
   */
  async downloadHistoricalTransactionsAsCsv(
    query: DownloadHistoricalTransactionsAsCsvQuery = {},
  ): Promise<ReadableStream<Uint8Array>> {
    return await this._api.transactions.downloadHistoricalTransactionsAsCsv({
      workspace_ids: query.workspaceIds?.length
        ? query.workspaceIds?.join(',')
        : undefined,
      recipient_user_id: query.recipientUserId,
      search_text: query.searchText,
      flags: query.flags?.map(mapTransactionFlagToResource).join(','),
      date_completed_from: query.dateCompleted?.from ?? undefined,
      date_completed_to: query.dateCompleted?.to ?? undefined,
    })
  }

  private _mapAdminHistoricalTransactionResourceToProps(
    resource: AdminHistoricalTransactionResource,
  ): Partial<AdminTransactionProps> {
    return {
      id: resource.id,
      shortId: resource.short_id,
      amount: resource.amount,
      iteration: resource.iteration,
      createdAt: maybeParseDate(resource.date_created),
      approvedAt: maybeParseDate(resource.date_approved),
      completedAt: maybeParseDate(resource.date_completed),
      approvedByUserId: resource.approved_by_user_id,
      payoutId: resource.payout_id,
      stripeAccountId: resource.stripe_account_id,
      stripeTransferId: resource.stripe_transfer_id,
      recipient: resource.recipient_user_id
        ? this._primeUser({ publicId: resource.recipient_user_id })
        : undefined,
      workspace: resource.workspace_id
        ? this._primeWorkspace({ id: resource.workspace_id })
        : undefined,
      type: mapTransactionTypeResourceToTransactionType(resource.type),
      source: mapTransactionSourceTypeResourceToTransactionSourceType(
        resource.source,
      ),
      reason:
        resource.reason &&
        mapUserTransactionReasonResourceToUserTransactionReason(
          resource.reason,
        ),
      memo: resource.memo,
      jobId: Id(resource.job_id),
      flags: resource.flags.map(mapTransactionFlagResourceToTransactionFlag),
    }
  }
}

/**
 * Lists historical transactions.
 */
export interface ListHistoricalTransactionsQuery extends CursorPageQuery {
  workspaceIds?: Id[] | null
  recipientUserId?: Id
  dateCompleted?: TransactionDateRange | null
  flags?: TransactionFlag[] | null
  searchText?: string
}

/**
 * Downloads historical transactions as a CSV.
 */
export interface DownloadHistoricalTransactionsAsCsvQuery {
  workspaceIds?: Id[] | null
  recipientUserId?: Id
  dateCompleted?: TransactionDateRange | null
  flags?: TransactionFlag[] | null
  searchText?: string
}
